import { Suspense } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ScrollToTop } from "@/components/ScrollToTop";
import { PixelRouteTracker } from "@/components/PixelRouteTracker";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import { useGroupNotifications } from "@/hooks/useGroupNotifications";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CookieConsent } from "@/components/CookieConsent";
import { AgeGate } from "@/components/AgeGate";
import { SupportChat } from "@/components/SupportChat";
import { ReloadPrompt } from "@/components/ReloadPrompt";
import { AuthenticatedLayout } from "@/components/AuthenticatedLayout";
import { TournamentLayout } from "@/components/TournamentLayout";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";
import { useUtmTracking } from "@/hooks/useUtmTracking";

// Eagerly loaded (critical path)
import Index from "./pages/Index.tsx";
import Auth from "./pages/Auth.tsx";
import NotFound from "./pages/NotFound.tsx";

// Lazy loaded (code-split)
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const WorldCupBracket = lazy(() => import("./pages/WorldCupBracket.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const OurStory = lazy(() => import("./pages/OurStory.tsx"));
const Schedule = lazy(() => import("./pages/Schedule.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const Bracket = lazy(() => import("./pages/Bracket.tsx"));
const Groups = lazy(() => import("./pages/Groups.tsx"));
const GroupDetail = lazy(() => import("./pages/GroupDetail.tsx"));
const ViewBracket = lazy(() => import("./pages/ViewBracket.tsx"));
const Profile = lazy(() => import("./pages/Profile.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));
const MyBrackets = lazy(() => import("./pages/MyBrackets.tsx"));
const Leaderboard = lazy(() => import("./pages/Leaderboard.tsx"));
const Install = lazy(() => import("./pages/Install.tsx"));
const Analytics = lazy(() => import("./pages/Analytics.tsx"));
const Compare = lazy(() => import("./pages/Compare.tsx"));
const Stats = lazy(() => import("./pages/Stats.tsx"));
const Widget = lazy(() => import("./pages/Widget.tsx"));
const InviteJoin = lazy(() => import("./pages/InviteJoin.tsx"));
const GoldenBoot = lazy(() => import("./pages/GoldenBoot.tsx"));
const Go = lazy(() => import("./pages/Go.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const CheckEmail = lazy(() => import("./pages/CheckEmail.tsx"));
const ContestRules = lazy(() => import("./pages/ContestRules.tsx"));
const Pricing = lazy(() => import("./pages/Pricing.tsx"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess.tsx"));
const Tournaments = lazy(() => import("./pages/Tournaments.tsx"));
const TournamentHome = lazy(() => import("./pages/TournamentHome.tsx"));
const TournamentBracket = lazy(() => import("./pages/TournamentBracket.tsx"));
const Affiliate = lazy(() => import("./pages/Affiliate.tsx"));
const LPWorldCup = lazy(() => import("./pages/LPWorldCup.tsx"));

// Keep fetched data fresh for 5 min and cached for 10 min. Without
// staleTime every route change refires every query (Dashboard, sidebar
// subscription, etc.), which is the biggest perceived-latency win on
// back/forward navigation.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

function GlobalListeners() {
  useGroupNotifications();
  return null;
}

function GuestCatchAllRedirect() {
  const location = useLocation();
  const next = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`/auth?next=${next}`} replace />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  useAffiliateTracking();
  useUtmTracking();

  // While auth is loading, don't render routes yet to avoid flash
  if (loading) return null;

  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
    <Routes>
      {user ? (
        /* ── Authenticated: sidebar layout ── */
        <Route element={<AuthenticatedLayout />}>
          <Route path="/" element={<Index />} />
          <Route path="/bracket" element={<Bracket />} />
          <Route path="/my-brackets" element={<MyBrackets />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/groups" element={<Groups />} />
          <Route path="/groups/:groupId" element={<GroupDetail />} />
          <Route path="/groups/:groupId/bracket/:userId" element={<ViewBracket />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/stats" element={<Stats />} />
          <Route path="/golden-boot" element={<GoldenBoot />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/world-cup-bracket" element={<WorldCupBracket />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/affiliate" element={<Affiliate />} />
          <Route path="/install" element={<Install />} />
          {/* Tournament hub + tournament-specific routes */}
          <Route path="/tournaments" element={<Tournaments />} />
          <Route path="/tournaments/:slug" element={<TournamentLayout />}>
            <Route index element={<TournamentHome />} />
            <Route path="bracket" element={<TournamentBracket />} />
            <Route path="leaderboard" element={<Leaderboard />} />
            <Route path="groups" element={<Groups />} />
            <Route path="groups/:groupId" element={<GroupDetail />} />
          </Route>
          {/* Public pages still accessible when logged in */}
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/checkout-success" element={<CheckoutSuccess />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/contest-rules" element={<ContestRules />} />
          <Route path="/our-story" element={<OurStory />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/invite/:code" element={<InviteJoin />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Magic-link landing: if the other tab signed in, CheckEmail
              auto-redirects authed users to /. Without this route the
              page 404'd in the authenticated layout. */}
          <Route path="/check-email" element={<CheckEmail />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      ) : (
        /* ── Guest: traditional header layout ── */
        <>
          <Route path="/" element={<Index />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/world-cup-bracket" element={<WorldCupBracket />} />
          <Route path="/lp/world-cup" element={<LPWorldCup />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/checkout-success" element={<CheckoutSuccess />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/contest-rules" element={<ContestRules />} />
          <Route path="/our-story" element={<OurStory />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/install" element={<Install />} />
          <Route path="/widget" element={<Widget />} />
          <Route path="/bracket" element={<Auth />} />
          <Route path="/invite/:code" element={<InviteJoin />} />
          <Route path="/go" element={<Go />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/check-email" element={<CheckEmail />} />
          {/* Tournament routes (public) */}
          <Route path="/tournaments" element={<Tournaments />} />
          <Route path="/tournaments/:slug" element={<TournamentLayout />}>
            <Route index element={<TournamentHome />} />
            <Route path="bracket" element={<TournamentBracket />} />
          </Route>
          {/* Any unmatched path for a guest is assumed to be a protected
              route (shared link, expired session). Redirect to /auth and
              remember where they were trying to go so we can send them
              there post-sign-in. */}
          <Route path="*" element={<GuestCatchAllRedirect />} />
        </>
      )}
    </Routes>
    </Suspense>
  );
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <GlobalListeners />
            <BrowserRouter>
              <ScrollToTop />
              <PixelRouteTracker />
              <AppRoutes />
              <AgeGate />
              <CookieConsent />
              <SupportChat />
              <ReloadPrompt />
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
